if (!customElements.get('pdp-product-media')) {
  class PdpProductMedia extends HTMLElement {
    constructor() {
      super();
      this.mainSwiper = null;
      this.thumbStrip = null;
      this._mainSlides = [];
      this._thumbItems = [];
      this._slidesByTab = {};
      this._thumbsByTab = {};
      this.currentFilter = 'product';
    }

    connectedCallback() {
      this.initWhenReady();
    }

    initWhenReady() {
      if (typeof Swiper !== 'undefined') {
        this.init();
      } else {
        document.querySelector('script[src*="swiper-bundle"]')?.addEventListener('load', () => {
          this.init();
        });
      }
    }

    init() {
      const mainEl = this.querySelector('.pdp-media__main-swiper');
      if (!mainEl) return;

      this.thumbStrip = this.querySelector('pdp-thumb-strip');

      // Cache all slide elements & categorise by tab
      this._mainSlides = [...mainEl.querySelectorAll('.swiper-slide')];
      this._thumbItems = this.thumbStrip ? [...this.thumbStrip.querySelectorAll('[data-thumb-index]')] : [];

      // Keep thumbs' data-tab in sync with slides' data-tab.
      // You only need to author `data-tab` on `.swiper-slide` in Liquid.
      const syncLen = Math.min(this._mainSlides.length, this._thumbItems.length);
      for (let i = 0; i < syncLen; i++) {
        const tab = this._mainSlides[i]?.dataset?.tab;
        if (tab) this._thumbItems[i].dataset.tab = tab;
      }

      // Init main swiper
      this.mainSwiper = new Swiper(mainEl, {
        slidesPerView: 1,
        spaceBetween: 0,
        speed: 400,
        watchSlidesProgress: true,
        lazyPreloaderClass: 'pdp-preloader',
        focusableElements: 'input, select, option, textarea, button, label',
        pagination: {
          el: this.querySelector('.pdp-media__dots'),
          clickable: true,
        },
        on: {
          slideChange: (swiper) => {
            this.handleSlideChange(swiper);
            if (this.thumbStrip) this.thumbStrip.setActive?.(swiper.activeIndex);
            this._announceSlide(swiper);
          },
          touchEnd: (swiper) => {
            const t = swiper.touches;
            if (!t) return;
            const diff = t.startX - t.currentX; // positive = swipe left (next)
            const threshold = 50;
            let switched = false;
            if (swiper.isBeginning && diff < -threshold) {
              switched = this._switchToAdjacentTab('prev');
            } else if (swiper.isEnd && diff > threshold) {
              switched = this._switchToAdjacentTab('next');
            }
            if (switched) {
              swiper.allowSlideNext = false;
              swiper.allowSlidePrev = false;
              requestAnimationFrame(() => {
                swiper.allowSlideNext = true;
                swiper.allowSlidePrev = true;
              });
            }
          },
        },
      });

      // Thumb strip → main swiper sync
      if (this.thumbStrip) {
        this.thumbStrip.addEventListener('thumb:change', (e) => {
          this.mainSwiper?.slideTo(e.detail.index);
        });
      }

      // Navigation: fully managed (no Swiper navigation module) to support cross-tab switching
      const prevBtn = this.querySelector('.pdp-media__nav-btn--prev');
      const nextBtn = this.querySelector('.pdp-media__nav-btn--next');
      if (prevBtn) {
        prevBtn.addEventListener('click', () => {
          if (!this.mainSwiper) return;
          if (this.mainSwiper.isBeginning) {
            this._switchToAdjacentTab('prev');
          } else {
            this.mainSwiper.slidePrev();
          }
        });
      }
      if (nextBtn) {
        nextBtn.addEventListener('click', () => {
          if (!this.mainSwiper) return;
          if (this.mainSwiper.isEnd) {
            this._switchToAdjacentTab('next');
          } else {
            this.mainSwiper.slideNext();
          }
        });
      }

      // Build tab-indexed maps & apply initial filter
      this._rebuildTabMaps();
      this.filterSlides('product');
      if (this.thumbStrip) this.thumbStrip.classList.add('is-ready');

      // Tab bar filtering
      const tabBar = this.querySelector('pdp-media-tabs');
      if (tabBar) {
        tabBar.addEventListener('tab:change', (e) => {
          this.currentFilter = e.detail.value;
          this.filterSlides(e.detail.value);
        });
      }

      // Parse variant media JSON (if present)
      this._variantMediaMap = null;
      const vmScript = this.querySelector('script[data-variant-media]');
      if (vmScript) {
        try {
          this._variantMediaMap = JSON.parse(vmScript.textContent);
        } catch (_) { }
      }

      // Listen for variant changes to swap media
      this._onVariantChanged = (evt) => {
        if (evt.isInit) return; // skip initial event triggered on page load
        const variant = evt.currentVariant || (evt.detail);
        if (!variant) return;

        if (this._variantMediaMap) {
          let media = this._variantMediaMap[String(variant.id)];
          if (typeof media === 'number') media = this._variantMediaMap._groups[media];
          if (media) {
            this._swapVariantMedia(media);
          } else {
            const defaultMedia = this._variantMediaMap['__default__'];
            if (defaultMedia) this._putVariantImageFirst(variant, defaultMedia);
          }
        } else {
          this._putVariantImageFirst(variant);
        }
      };
      document.addEventListener('variant:changed', this._onVariantChanged);

      // Image click → open fullscreen lightbox
      this.initImageLightbox();

      // Tab bar: show on hover, hide on leave / idle (opt-in via data-tabs-auto-hide, desktop/mouse only)
      if (this.hasAttribute('data-tabs-auto-hide') && window.luteUtil?.isMouseDevice()) {
        this._tabIdleTimer = null;
        this._tabIdleDelay = 3000;
        const mainArea = this.querySelector('.pdp-media__main');
        if (mainArea) {
          mainArea.addEventListener('mouseenter', () => this._showTabs(mainArea));
          mainArea.addEventListener('mousemove', () => this._showTabs(mainArea));
          mainArea.addEventListener('mouseleave', () => this._hideTabs(mainArea));
        }
      }
    }

    disconnectedCallback() {
      if (this._onVariantChanged) {
        document.removeEventListener('variant:changed', this._onVariantChanged);
      }
    }

    /** Announce current slide for screen readers */
    _announceSlide(swiper) {
      const announcer = this.querySelector('[data-slide-announcer]');
      if (!announcer) return;
      const total = swiper.slides.length;
      const current = swiper.activeIndex + 1;
      const slide = swiper.slides[swiper.activeIndex];
      const alt = slide?.querySelector('img')?.alt || '';
      announcer.textContent = `Image ${current} of ${total}${alt ? ': ' + alt : ''}`;
    }

    _showTabs(el) {
      clearTimeout(this._tabIdleTimer);
      el.classList.add('is-tabs-visible');
      this._tabIdleTimer = setTimeout(() => this._hideTabs(el), this._tabIdleDelay);
    }

    _hideTabs(el) {
      clearTimeout(this._tabIdleTimer);
      el.classList.remove('is-tabs-visible');
    }

    /** Returns ordered array of visible tab values, e.g. ['product','details','video'] */
    _getVisibleTabs() {
      const tabBar = this.querySelector('pdp-media-tabs');
      if (!tabBar) return [this.currentFilter];
      return [...tabBar.querySelectorAll('button[data-value]:not([hidden])')].map((b) => b.dataset.value);
    }

    /** Returns the adjacent visible tab name in the given direction, or null if at boundary */
    _getAdjacentTab(direction) {
      const tabs = this._getVisibleTabs();
      const idx = tabs.indexOf(this.currentFilter);
      if (idx === -1) return null;
      return direction === 'next' ? (tabs[idx + 1] || null) : (tabs[idx - 1] || null);
    }

    /** Switch to an adjacent tab. direction: 'prev' | 'next' */
    _switchToAdjacentTab(direction) {
      const targetTab = this._getAdjacentTab(direction);
      if (!targetTab) return false;

      const goToEnd = direction === 'prev';
      const tabBar = this.querySelector('pdp-media-tabs');
      if (tabBar) tabBar.value = targetTab;
      this.currentFilter = targetTab;
      this.filterSlides(targetTab, goToEnd);
      return true;
    }

    /** Manually manage disabled/hidden state on prev/next nav btns */
    _updateNavBtns() {
      if (!this.mainSwiper) return;
      const prevBtn = this.querySelector('.pdp-media__nav-btn--prev');
      const nextBtn = this.querySelector('.pdp-media__nav-btn--next');
      if (!prevBtn || !nextBtn) return;

      const totalSlides = this.mainSwiper.slides.length;
      const tabs = this._getVisibleTabs();
      // Hide both buttons when only 1 slide and 1 tab (nothing to navigate)
      const shouldLock = totalSlides <= 1 && tabs.length <= 1;
      prevBtn.classList.toggle('swiper-button-lock', shouldLock);
      nextBtn.classList.toggle('swiper-button-lock', shouldLock);
      if (shouldLock) return;

      const isFirst = this.mainSwiper.isBeginning;
      const isLast = this.mainSwiper.isEnd;
      const hasPrevTab = this._getAdjacentTab('prev') !== null;
      const hasNextTab = this._getAdjacentTab('next') !== null;

      // Disable prev when at first slide AND no previous tab
      prevBtn.classList.toggle('swiper-button-disabled', isFirst && !hasPrevTab);
      prevBtn.toggleAttribute('aria-disabled', isFirst && !hasPrevTab);
      // Disable next when at last slide AND no next tab
      nextBtn.classList.toggle('swiper-button-disabled', isLast && !hasNextTab);
      nextBtn.toggleAttribute('aria-disabled', isLast && !hasNextTab);
    }

    /** Rebuild tab-indexed lookup maps from flat _mainSlides / _thumbItems arrays. */
    _rebuildTabMaps() {
      this._slidesByTab = {};
      this._thumbsByTab = {};
      for (const s of this._mainSlides) {
        const tab = s.dataset.tab || 'product';
        (this._slidesByTab[tab] ??= []).push(s);
      }
      for (const t of this._thumbItems) {
        const tab = t.dataset.tab || 'product';
        (this._thumbsByTab[tab] ??= []).push(t);
      }
    }

    filterSlides(filter, goToEnd = false) {
      if (!this.mainSwiper) return;

      const slides = this._slidesByTab[filter] || [];
      const thumbs = this._thumbsByTab[filter] || [];

      // Swiper API: clear + batch append (single internal update, no manual DOM)
      this.mainSwiper.removeAllSlides();
      if (slides.length) this.mainSwiper.appendSlide(slides);

      const count = this.mainSwiper.slides.length;
      const targetIdx = goToEnd && count > 0 ? count - 1 : 0;
      this.mainSwiper.slideTo(0, 0, false);
      this.mainSwiper.slideTo(targetIdx, 0);

      // Sync thumb strip
      if (this.thumbStrip) {
        const frag = document.createDocumentFragment();
        thumbs.forEach((t, i) => {
          t.dataset.thumbIndex = i;
          frag.appendChild(t);
        });
        while (this.thumbStrip.firstChild) this.thumbStrip.firstChild.remove();
        this.thumbStrip.appendChild(frag);
        this.thumbStrip.setActive?.(targetIdx, goToEnd, false);
      }

      this._updateNavBtns();
    }

    /* ── Variant Media Swap ── */

    _swapVariantMedia(media) {
      // Pause any existing videos
      this.querySelectorAll('.pdp-media__main-swiper video').forEach((v) => {
        if (!v.paused) v.pause();
      });

      // Build new slides & thumbs
      const newSlides = [];
      const newThumbs = [];
      let idx = 0;

      media.product.forEach((item) => {
        newSlides.push(this._createImageSlide(item, 'product', 'image'));
        newThumbs.push(this._createThumbBtn('product', 'image', item.thumb, item.alt, idx));
        idx++;
      });
      media.details.forEach((item) => {
        newSlides.push(this._createImageSlide(item, 'details', 'details'));
        newThumbs.push(this._createThumbBtn('details', 'image', item.thumb, item.alt, idx));
        idx++;
      });
      media.video.forEach((item) => {
        newSlides.push(this._createVideoSlide(item));
        newThumbs.push(this._createThumbBtn('video', 'video', item.thumb || item.poster, '', idx));
        idx++;
      });

      // Remove old elements
      this._mainSlides.forEach((s) => s.remove());
      this._thumbItems.forEach((s) => s.remove());

      // Store new references & rebuild tab maps
      this._mainSlides = newSlides;
      this._thumbItems = newThumbs;
      this._rebuildTabMaps();

      // Update tab visibility
      const tabBar = this.querySelector('pdp-media-tabs');
      if (tabBar) {
        const detailsBtn = tabBar.querySelector('[data-value="details"]');
        const videoBtn = tabBar.querySelector('[data-value="video"]');
        if (detailsBtn) detailsBtn.hidden = media.details.length === 0;
        if (videoBtn) videoBtn.hidden = media.video.length === 0;
        tabBar._checkVisibility();
      }

      // Reset to product tab
      this.currentFilter = 'product';
      if (tabBar) tabBar.value = 'product';
      this.filterSlides('product');

    }

    _putVariantImageFirst(variant, mediaData) {
      const featuredSrc = (variant.featured_image || variant.featured_media?.preview_image)?.src;

      if (mediaData) {
        if (featuredSrc) {
          const featuredFile = featuredSrc.replace(/\?.*$/, '').split('/').pop();
          const product = mediaData.product;
          const matchIdx = product.findIndex(
            (item) => item.src.replace(/\?.*$/, '').split('/').pop() === featuredFile
          );
          if (matchIdx > 0) {
            mediaData = { ...mediaData, product: [product[matchIdx], ...product.slice(0, matchIdx), ...product.slice(matchIdx + 1)] };
          }
        }
        this._swapVariantMedia(mediaData);
        return;
      }

      if (!this.mainSwiper || !featuredSrc) return;
      const featuredFile = featuredSrc.replace(/\?.*$/, '').split('/').pop();
      const matchIdx = this._mainSlides.findIndex((s) => {
        if (s.dataset.tab !== 'product') return false;
        const img = s.querySelector('img');
        return img && img.src.replace(/\?.*$/, '').split('/').pop() === featuredFile;
      });
      if (matchIdx > 0) {
        const [slide] = this._mainSlides.splice(matchIdx, 1);
        this._mainSlides.unshift(slide);
        if (this._thumbItems[matchIdx]) {
          const [thumb] = this._thumbItems.splice(matchIdx, 1);
          this._thumbItems.unshift(thumb);
        }
        this._rebuildTabMaps();
      }
      if (this.currentFilter !== 'product') {
        this.currentFilter = 'product';
        const tabBar = this.querySelector('pdp-media-tabs');
        if (tabBar) tabBar.value = 'product';
      }
      this.filterSlides('product');
    }

    _createImageSlide(item, tab, mediaType) {
      const div = document.createElement('div');
      div.className = 'swiper-slide';
      div.dataset.tab = tab;
      div.dataset.mediaType = mediaType;
      div.appendChild(this._createPreloader());
      const img = document.createElement('img');
      img.src = item.src;
      img.alt = item.alt || '';
      img.loading = 'lazy';
      div.appendChild(img);
      return div;
    }

    _createPreloader() {
      const span = document.createElement('span');
      span.className = 'pdp-preloader';
      return span;
    }

    _createVideoSlide(item) {
      const div = document.createElement('div');
      div.className = 'swiper-slide';
      div.dataset.tab = 'video';
      div.dataset.mediaType = 'video';
      const video = document.createElement('video');
      video.playsInline = true;
      video.controls = true;
      video.preload = 'metadata';
      if (item.poster) video.poster = item.poster;
      const primarySrc = (item.sources && item.sources[0]) ? item.sources[0].src : '';
      if (primarySrc) video.src = primarySrc;
      div.appendChild(video);
      return div;
    }

    _createThumbBtn(tab, mediaType, src, alt, index) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'pdp-thumb-strip__item';
      btn.dataset.tab = tab;
      btn.dataset.thumbIndex = index;
      btn.dataset.mediaType = mediaType;
      btn.setAttribute('aria-label', alt || 'Image');
      const img = document.createElement('img');
      img.src = src;
      img.alt = alt || '';
      img.width = 80;
      img.height = 80;
      img.loading = 'lazy';
      img.draggable = false;
      btn.appendChild(img);
      return btn;
    }

    handleSlideChange(swiper) {
      // Pause all videos when sliding away
      const videos = this.querySelectorAll('.pdp-media__main-swiper video');
      videos.forEach((video) => {
        if (!video.paused) video.pause();
      });
      this._updateNavBtns();
    }

    /**
     * Collect lightbox-ready items from visible slides.
     * @param {Element} container - element containing .swiper-slide children
     * @param {Element} [clickedSlide] - the slide that was clicked (to resolve startIndex)
     * @returns {{ items: Array, startIndex: number }}
     */
    collectLightboxItems(container, clickedSlide) {
      const slides = [...container.querySelectorAll('.swiper-slide')];
      const items = [];
      let startIndex = 0;

      for (const s of slides) {
        if (s.dataset.mediaType === 'video') continue;
        const item = this._slideToLightboxItem(s, s.dataset.mediaType);
        if (!item) continue;

        if (s === clickedSlide) startIndex = items.length;
        items.push(item);
      }

      return { items, startIndex };
    }

    /**
     * Extract raw media data from a slide element.
     * Returns a plain descriptor — rendering is handled by PdpImageLightbox.
     * @param {Element} slide
     * @param {string} type - media type from data-media-type
     * @returns {object|null}
     */
    _slideToLightboxItem(slide, type) {
      if (type === 'image' || type === 'details') {
        const img = slide.querySelector('img');
        if (!img) return null;
        return {
          type: 'image',
          src: img.src.replace(/width=\d+/, 'width=2400'),
          thumb: img.src,
          alt: img.alt || '',
          w: img.naturalWidth || parseInt(img.getAttribute('width')) || 1200,
          h: img.naturalHeight || parseInt(img.getAttribute('height')) || 1200,
        };
      }

      if (type === 'video') {
        const video = slide.querySelector('video');
        if (!video) return null;
        const src = video.src || [...video.querySelectorAll('source')].map((s) => ({
          src: s.getAttribute('src'),
          type: s.getAttribute('type'),
        }));
        const poster = video.poster || '';
        return {
          type: 'video',
          sources: Array.isArray(src) ? src : [{ src: src, type: 'video/mp4' }],
          poster: poster,
          alt: '',
          thumb: poster,
        };
      }

      if (type === 'model') {
        const viewer = slide.querySelector('model-viewer');
        if (!viewer) return null;
        return {
          type: 'model',
          src: viewer.getAttribute('src') || '',
          poster: viewer.getAttribute('poster') || '',
          alt: viewer.getAttribute('alt') || '',
          thumb: viewer.getAttribute('poster') || '',
        };
      }

      return null;
    }

    /**
     * Load the lightbox section (if needed) and open it.
     * @param {number} startIndex
     * @param {Array} items
     */
    openLightbox(startIndex, items) {
      if (!items.length) return;

      const loader = this.querySelector('pdp-section-loader[data-section-id="pdp-image-lightbox"]');
      if (!loader) return;

      const mainEl = this.querySelector('.pdp-media__main-swiper');

      const doOpen = () => {
        if (mainEl) {
          mainEl.querySelectorAll('video').forEach((v) => { if (!v.paused) v.pause(); });
        }
        const lightbox = this.querySelector('pdp-image-lightbox');
        if (lightbox) lightbox.open(startIndex, items);
      };

      if (loader.hasAttribute('loaded')) {
        doOpen();
      } else if (!loader.hasAttribute('loading')) {
        loader.addEventListener('pdp-section:loaded', () => doOpen(), { once: true });
        loader.reload();
      }
    }

    initImageLightbox() {
      const mainEl = this.querySelector('.pdp-media__main-swiper');
      if (!mainEl) return;

      mainEl.addEventListener('click', (e) => {
        const slide = e.target.closest('.swiper-slide');
        if (!slide) return;
        const mediaType = slide.dataset.mediaType;

        if (mediaType === 'video') return;
        if (!mediaType) return;

        const { items, startIndex } = this.collectLightboxItems(mainEl, slide);

        this.openLightbox(startIndex, items);
      });
    }

  }

  customElements.define('pdp-product-media', PdpProductMedia);
}

if (!customElements.get('pdp-media-tabs')) {
  class PdpMediaTabs extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      this._slider = this.querySelector('.pdp-media-tabs__slider');
      this.addEventListener('click', this);
      this.addEventListener('keydown', this);
      this._moveSlider(this.querySelector('button[aria-selected="true"]'), false);
      this.classList.add('is-ready');
      this._checkVisibility();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this);
      this.removeEventListener('keydown', this);
    }

    handleEvent(e) {
      if (e.type === 'keydown') {
        this._handleKeydown(e);
        return;
      }
      if (e.type !== 'click') return;
      const btn = e.target.closest('button[data-value]');
      if (!btn || btn.getAttribute('aria-selected') === 'true') return;

      this._selectTab(btn);
    }

    _handleKeydown(e) {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      const tabs = [...this.querySelectorAll('button[data-value]:not([hidden])')];
      const current = tabs.findIndex(t => t.getAttribute('aria-selected') === 'true');
      if (current === -1) return;
      e.preventDefault();
      const next = e.key === 'ArrowRight'
        ? (current + 1) % tabs.length
        : (current - 1 + tabs.length) % tabs.length;
      this._selectTab(tabs[next]);
      tabs[next].focus();
    }

    _selectTab(btn) {
      this.querySelectorAll('button[data-value]').forEach((b) => {
        b.setAttribute('aria-selected', 'false');
        b.setAttribute('tabindex', '-1');
      });
      btn.setAttribute('aria-selected', 'true');
      btn.setAttribute('tabindex', '0');
      this._moveSlider(btn, true);

      this.dispatchEvent(
        new CustomEvent('tab:change', {
          bubbles: true,
          detail: { value: btn.dataset.value },
        })
      );
    }

    _moveSlider(btn, animate) {
      if (!btn || !this._slider) return;
      if (!animate) this._slider.style.transition = 'none';
      this._slider.style.width = btn.offsetWidth + 'px';
      this._slider.style.transform = `translateX(${btn.offsetLeft}px)`;
      if (!animate) {
        this._slider.offsetHeight; /* force reflow */
        this._slider.style.transition = '';
      }
    }

    _checkVisibility() {
      const visible = this.querySelectorAll('button[data-value]:not([hidden])');
      this.closest('.pdp-media__tabs').hidden = visible.length <= 1;
    }

    get value() {
      return this.querySelector('button[aria-selected="true"]')?.dataset.value || null;
    }

    set value(v) {
      const btn = this.querySelector(`button[data-value="${v}"]`);
      if (btn && btn.getAttribute('aria-selected') !== 'true') {
        this.querySelectorAll('button[data-value]').forEach((b) => {
          b.setAttribute('aria-selected', 'false');
          b.setAttribute('tabindex', '-1');
        });
        btn.setAttribute('aria-selected', 'true');
        btn.setAttribute('tabindex', '0');
        this._moveSlider(btn, true);
      }
    }
  }

  customElements.define('pdp-media-tabs', PdpMediaTabs);
}

if (!customElements.get('pdp-coupon')) {
  class PdpCoupon extends HTMLElement {
    _readConfigAndElements() {
      const dataScript = this.querySelector('script[data-pdp-coupon-data]');
      if (!dataScript) return null;

      const config = JSON.parse(dataScript.textContent);
      const checkboxEl = this.querySelector('.pdp-coupon__checkbox-wrap');
      const inputEl = this.querySelector('[data-pdp-coupon-checkbox]');
      const wrapEl = this.querySelector('.pdp-coupon__wrap');

      if (!checkboxEl || !inputEl || !wrapEl) return null;

      this._config = config;
      this._inputEl = inputEl;
      this._wrapEl = wrapEl;
      this._checkboxEl = checkboxEl;

      return { config, inputEl, wrapEl };
    }

    async _updateValueDisplay() {
      const config = this._config;
      const labelEl = this.querySelector('[data-pdp-coupon-label]');
      if (!labelEl) return;

      if (this._discountType === 'percentage') {
        labelEl.innerHTML = config.i18n.savePercentWithCode
          .replace('__PERCENT__', this._discountValue)
          .replace('__CODE__', this._discountCode);
      } else {
        // Keep legacy formatting behavior: fmt expects cents.
        const discountValueWithCureey = await window.__pdp__config__.fmt(this._discountValue * 100);
        labelEl.innerHTML = config.i18n.saveAmountWithCode
          .replace('__AMOUNT__', discountValueWithCureey)
          .replace('__CODE__', this._discountCode);
      }
    }

    _applyDiscount() {
      if (this._discountApplied) return;
      this._discountApplied = true;

      fetch(Shopify.routes.root + 'discount/' + encodeURIComponent(this._discountCode));

      const cartCountEl = document.querySelector('[data-cart-count]');
      if (cartCountEl && parseInt(cartCountEl.textContent) > 0) {
        fetch(Shopify.routes.root + 'cart.js')
          .then(function (r) {
            return r.json();
          })
          .then(function (data) {
            const codes = (data.discount_codes || []).map(function (x) {
              return x.code;
            });
            if (!codes.includes(this._discountCode)) {
              codes.push(this._discountCode);
            }

            const cartDiscountEl = document.querySelector('[data-cart-discount]');
            if (cartDiscountEl) {
              cartDiscountEl.value = codes.join(',');
            }

            return fetch(Shopify.routes.root + 'cart/update.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ discount: codes.join(',') }),
            });
          }.bind(this));
      }

      this.dispatchEvent(
        new CustomEvent('pdp:coupon-applied', {
          bubbles: true,
          detail: { discountCode: this._discountCode, discountType: this._discountType, discountValue: this._discountValue },
        })
      );
    }

    _removeDiscount() {
      this._discountApplied = false;
      this.dispatchEvent(
        new CustomEvent('pdp:coupon-removed', {
          bubbles: true,
          detail: { discountCode: this._discountCode },
        })
      );
    }

    _updateCouponUI(newCode, newType, newValue) {
      this._discountCode = newCode;
      this._discountType = newType;
      this._discountValue = parseFloat(newValue);
      this._discountApplied = false;

      const copyBtn = this.querySelector('[data-pdp-coupon-copy]');
      if (copyBtn) copyBtn.dataset.content = newCode;

      // Keep behavior: fire and forget (original code didn't await).
      this._updateValueDisplay();

      if (this._isChecked) {
        this._applyDiscount();
      }
    }

    _handleVariantChange(currentVariant) {
      if (!currentVariant) return;

      this._setCheckboxVisibility(this._hiddenCheckboxVariantIds.has(String(currentVariant.id)));

      const vDiscount = this._variantDiscountMap[currentVariant.id];

      if (vDiscount) {
        this._updateCouponUI(vDiscount.code, vDiscount.type, vDiscount.value);
        this._setCouponVisibility(true);
        return;
      }

      if (this._basicDiscount.code && this._basicDiscount.value && this._basicDiscount.type) {
        this._updateCouponUI(this._basicDiscount.code, this._basicDiscount.type, this._basicDiscount.value);
        this._setCouponVisibility(true);
        return;
      }

      if (this._isChecked) {
        this._isChecked = false;
        this._inputEl.checked = false;
        this._removeDiscount();
      }
      this._setCouponVisibility(false);
    }

    _applyDiscountIfChecked() {
      if (!this._inputEl.checked) return;
      if (this._checkboxHidden) return;
      this._isChecked = true;
      this._discountApplied = false;
      this._applyDiscount();
    }

    _setCheckboxVisibility(shouldHide) {
      this._checkboxHidden = !!shouldHide;
      if (this._checkboxEl) this._checkboxEl.hidden = this._checkboxHidden;

      if (this._checkboxHidden) {
        if (this._isChecked || this._inputEl.checked) {
          this._isChecked = false;
          this._inputEl.checked = false;
          this._removeDiscount();
        }
        this._inputEl.disabled = true;
        return;
      }

      this._inputEl.disabled = false;
    }

    _setCouponVisibility(visible) {
      this.hidden = !visible;
      this.classList.toggle('is-hidden', !visible);
    }

    _bindWrapClick() {
      const inputEl = this._inputEl;
      const wrapEl = this._wrapEl;
      wrapEl.addEventListener('click', (e) => {
        if (e.target.closest('[data-pdp-coupon-copy]')) return;
        if (this._checkboxHidden) return;
        if (e.target !== inputEl) {
          e.preventDefault();
          inputEl.checked = !inputEl.checked;
        }
        this._isChecked = inputEl.checked;
        if (this._isChecked) this._applyDiscount();
        else this._removeDiscount();
      });
    }

    _bindVariantChanged() {
      this._onVariantChanged = (evt) => {
        if (evt.isInit) return;
        this._handleVariantChange(evt.currentVariant || (evt.detail && evt.detail.variant));
      };
      document.addEventListener('variant:changed', this._onVariantChanged);
    }

    _bindPageShow() {
      this._onPageShow = (e) => {
        // bfcache: JS heap + DOM restored. Skip internal updates to avoid race conditions.
        if (e.persisted) return;

        this._applyDiscountIfChecked();
        requestAnimationFrame(() => {
          this._applyDiscountIfChecked();
          requestAnimationFrame(() => {
            this._applyDiscountIfChecked();
          });
        });
      };
      window.addEventListener('pageshow', this._onPageShow);
    }

    connectedCallback() {
      const init = this._readConfigAndElements();
      if (!init) return;

      const { config } = init;

      // State
      this._isChecked = false;
      this._discountApplied = false;
      this._discountCode = config.discountCode;
      this._discountType = config.discountType;
      this._discountValue = parseFloat(config.discountValue);

      this._basicDiscount = {
        code: config.basicDiscountCode,
        type: config.basicDiscountType,
        value: parseFloat(config.basicDiscountValue),
      };

      if (this._basicDiscount.type === 'amount' && config.crossCurrency) {
        this._basicDiscount.value = this._basicDiscount.value * parseFloat(Shopify.currency.rate);
      }

      this._variantDiscountMap = {};
      config.variantDiscounts.forEach((item) => {
        const parts = item.codeValue.split(';;;;');
        const code = parts[0];
        const valuePart = parts[1];
        let value = parseFloat(valuePart.split('--')[1]);
        const type = valuePart.includes('P--') ? 'percentage' : 'amount';
        if (type === 'amount' && config.crossCurrency) {
          value = value * parseFloat(Shopify.currency.rate);
        }
        this._variantDiscountMap[item.variantId] = { code, type, value };
      });

      this._hiddenCheckboxVariantIds = new Set(
        (config.hiddenCheckboxVariantIds || []).map((id) => String(id))
      );
      this._checkboxHidden = false;
      this._setCheckboxVisibility(this._hiddenCheckboxVariantIds.has(String(config.currentVariantId || '')));

      // Main discount conversion
      if (this._discountType === 'amount' && config.crossCurrency) {
        this._discountValue = this._discountValue * parseFloat(Shopify.currency.rate);
        this._updateValueDisplay();
      }

      this._bindWrapClick();
      this._bindVariantChanged();
      this._handleVariantChange({ id: config.currentVariantId });

      // Sync state if browser restored the checkbox (bfcache / back-forward)
      // Defer so PdpPrice has time to attach its event listeners first.
      if (this._inputEl.checked && !this._isChecked) {
        this._isChecked = true;
        setTimeout(() => this._applyDiscount(), 0);
      }

      this._bindPageShow();
    }

    disconnectedCallback() {
      if (this._onVariantChanged) {
        document.removeEventListener('variant:changed', this._onVariantChanged);
      }
      if (this._onPageShow) {
        window.removeEventListener('pageshow', this._onPageShow);
      }
    }
  }
  customElements.define('pdp-coupon', PdpCoupon);
}

/* ===== PDP Installment Klarna Web Component ===== */
if (!customElements.get('pdp-installment-klarna')) {
  const KLARNA_SDK_URL = 'https://js.klarna.com/web-sdk/v1/klarna.js';
  const KLARNA_READY_CALLBACK = 'pdpKlarnaSDKReady';
  let klarnaReadyPromise = null;

  function getKlarnaReadyPromise() {
    if (window.Klarna?.OnsiteMessaging?.on) return Promise.resolve(window.Klarna);

    if (!klarnaReadyPromise) {
      klarnaReadyPromise = new Promise((resolve) => {
        const previousCallback = window[KLARNA_READY_CALLBACK];
        window[KLARNA_READY_CALLBACK] = (klarna) => {
          if (typeof previousCallback === 'function') previousCallback(klarna);
          resolve(klarna || window.Klarna);
        };
      });
    }

    return klarnaReadyPromise;
  }

  function getKlarnaSDKUrl(src) {
    const url = new URL(src, window.location.href);
    url.searchParams.set('ready', KLARNA_READY_CALLBACK);
    return url.href;
  }

  class PdpInstallmentKlarna extends HTMLElement {
    static get observedAttributes() {
      return ['data-price'];
    }

    connectedCallback() {
      if (this._init) return;
      this._init = true;
      this._placement = this.querySelector('klarna-placement');
      if (!this._placement) {
        this._hideLoading();
        return;
      }

      this._syncPrice(this.getAttribute('data-price'));
      this._initPlacementWatch();
      this._loadSDK();
    }

    attributeChangedCallback(name, oldValue, newValue) {
      if (name !== 'data-price' || oldValue === newValue || !this._init) return;
      if (this._syncPrice(newValue)) this._refreshPlacement();
    }

    _syncPrice(value) {
      const price = parseInt(value, 10);
      if (isNaN(price) || !this._placement) return false;
      this._placement.setAttribute('data-purchase-amount', String(price));
      return true;
    }

    _loadSDK() {
      const clientId = this.dataset.clientId;
      if (!clientId || !window.luteUtil?.loadLibrary) {
        this._hideLoading();
        return;
      }

      const readyPromise = getKlarnaReadyPromise();

      window.luteUtil.loadLibrary({
        src: getKlarnaSDKUrl(this.dataset.src || KLARNA_SDK_URL),
        type: 'script',
        attrs: {
          'data-environment': this.dataset.environment || 'production',
          'data-client-id': clientId,
        },
      }).then(() => {
        return readyPromise;
      }).then(() => {
        this._initPlacementWatch();
      }).catch(() => {
        this._hideLoading();
      });
    }

    _isPlacementRendered() {
      const placement = this._placement;
      if (!placement || !placement.isConnected) return false;

      const host = placement.nextElementSibling;
      if (host?.shadowRoot?.childElementCount > 0) return true;
      if (placement.shadowRoot?.childElementCount > 0) return true;

      const { height, width } = placement.getBoundingClientRect();
      return height > 4 && width > 40;
    }

    _observePlacementRender() {
      if (this._placementObserver || !this._placement) return;

      const checkRendered = () => {
        if (this._isPlacementRendered()) this._hideLoading();
      };

      this._placementObserver = new MutationObserver(checkRendered);
      this._placementObserver.observe(this, { childList: true, subtree: true });
      checkRendered();
    }

    _refreshPlacement() {
      const refresh = window.Klarna?.OnsiteMessaging?.refresh;
      if (typeof refresh === 'function') refresh.call(window.Klarna.OnsiteMessaging);
    }

    _initPlacementWatch() {
      if (!this._placementWatchStarted) {
        this._placementWatchStarted = true;
        this._observePlacementRender();
        this._scheduleLoadingFallback();
      }

      this._refreshPlacement();
      if (this._isPlacementRendered()) this._hideLoading();
    }

    _scheduleLoadingFallback() {
      if (this._loadingFallbackTimer) window.clearTimeout(this._loadingFallbackTimer);
      this._loadingFallbackTimer = window.setTimeout(() => this._hideLoading(), 8000);
    }

    _showLoading() {
      this.classList.remove('is-ready');
      const loading = this.querySelector('[data-pdp-installment-loading]');
      if (loading) loading.classList.remove('is-hidden');
    }

    _hideLoading() {
      if (this._loadingFallbackTimer) {
        window.clearTimeout(this._loadingFallbackTimer);
        this._loadingFallbackTimer = null;
      }
      this.classList.add('is-ready');
      const loading = this.querySelector('[data-pdp-installment-loading]');
      if (loading) loading.classList.add('is-hidden');
    }

    disconnectedCallback() {
      if (this._placementObserver) {
        this._placementObserver.disconnect();
        this._placementObserver = null;
      }
      if (this._loadingFallbackTimer) {
        window.clearTimeout(this._loadingFallbackTimer);
        this._loadingFallbackTimer = null;
      }
    }
  }

  customElements.define('pdp-installment-klarna', PdpInstallmentKlarna);
}

/* ===== PDP Installment PayPal Web Component ===== */
if (!customElements.get('pdp-installment-paypal')) {
  class PdpInstallmentPaypal extends HTMLElement {
    static get observedAttributes() {
      return ['data-price'];
    }

    connectedCallback() {
      if (this._init) return;
      this._init = true;
      this._ppMsg = this.querySelector('[data-pdp-installment-paypal]');
      if (!this._ppMsg) return;

      this._loadAndRender();
    }

    attributeChangedCallback(name, oldValue, newValue) {
      if (name !== 'data-price' || oldValue === newValue || !this._init) return;
      const price = parseInt(newValue, 10);
      if (!isNaN(price)) this._renderMessage(price);
    }

    _loadAndRender() {
      const clientId = this.dataset.clientId;
      if (!clientId || !window.luteUtil?.loadLibrary) {
        this._hidePaypal();
        return;
      }

      window.luteUtil
        .loadLibrary({
          src: `https://www.paypal.com/sdk/js?client-id=${encodeURIComponent(clientId)}&components=messages`,
          type: 'script',
        })
        .then(() => {
          const price = parseInt(this.getAttribute('data-price'), 10);
          if (!isNaN(price)) this._renderMessage(price);
        })
        .catch(() => {
          this._hidePaypal();
        });
    }

    _renderMessage(priceInCents) {
      if (!this._ppMsg || typeof paypal === 'undefined' || !paypal.Messages) {
        return;
      }

      paypal
        .Messages({
          amount: priceInCents / 100,
          pageType: 'product-details',
          buyerCountry: this.dataset.country,
          locale: this.dataset.locale,
          currency: this.dataset.currency,
        })
        .render(this._ppMsg)
        .then(() => {
          const loading = this.querySelector('[data-pdp-installment-loading]');
          if (loading) loading.classList.add('is-hidden');
          const wrap = this._ppMsg.closest('.pdp-product__installment-pp-wrap');
          if (wrap) wrap.hidden = false;
        })
        .catch(() => {
          this._hidePaypal();
        });
    }

    _hidePaypal() {
      const wrap = this._ppMsg?.closest('.pdp-product__installment-pp-wrap');
      if (wrap) wrap.hidden = true;
      const loading = this.querySelector('[data-pdp-installment-loading]');
      if (loading) loading.classList.add('is-hidden');
    }

    disconnectedCallback() { }
  }

  customElements.define('pdp-installment-paypal', PdpInstallmentPaypal);
}

/* ===== PDP Collapsible Web Component ===== */
if (!customElements.get('pdp-collapsible')) {
  let _pdpCollapsibleUid = 0;

  class PdpCollapsible extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;

      this._toggle = this.querySelector('[data-toggle]');
      this._panel = this.querySelector('.pdp-collapsible__panel');

      /* Generate unique IDs for aria-controls association */
      if (this._toggle && this._panel) {
        const uid = 'pdp-collapse-' + (++_pdpCollapsibleUid);
        this._panel.id = uid;
        this._toggle.setAttribute('aria-controls', uid);
      }

      if (this._toggle) {
        this._toggle.setAttribute('aria-expanded', String(this.open));
        this._toggle.addEventListener('click', () => this.handleToggle());
      }
    }

    handleToggle() {
      this.open = !this.open;
    }

    get open() {
      return this.hasAttribute('open');
    }

    set open(val) {
      const prev = this.open;
      if (val) {
        this.setAttribute('open', '');
      } else {
        this.removeAttribute('open');
      }
      if (this._toggle) {
        this._toggle.setAttribute('aria-expanded', String(!!val));
      }
      if (prev !== !!val) {
        this.dispatchEvent(new CustomEvent('toggle', { detail: { open: !!val }, bubbles: true }));

        if (val) {
          const parent = this.closest('[data-pdp-accordion]');
          if (parent) {
            parent.querySelectorAll('pdp-collapsible[open]').forEach((sibling) => {
              if (sibling !== this) sibling.open = false;
            });
          }
        }
      }
    }
  }

  customElements.define('pdp-collapsible', PdpCollapsible);
}

/* ===== PDP Info Modal Web Component ===== */
if (!customElements.get('pdp-info-modal')) {
  class PdpInfoModal extends HTMLElement {
    constructor() {
      super();
      this._overlay = null;
      this._titleEl = null;
      this._bodyEl = null;
      this._slots = {};
      this._customClass = []
    }

    static get observedAttributes() {
      return ['open'];
    }

    connectedCallback() {
      this._slots = {};
      for (const tpl of this.querySelectorAll('template')) {
        const name = tpl.getAttribute('slot') || 'body';
        this._slots[name] = tpl.innerHTML;
      }
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this);
      if (this._overlay) {
        this._overlay.removeEventListener('click', this);
        this._overlay.remove();
        this._overlay = null;
      }
    }

    /* — handleEvent: single event router — */
    handleEvent(e) {
      switch (e.type) {
        case 'click':
          if (e.target === this._overlay || e.target.closest('[data-close]')) {
            this.open = false;
          }
          break;
        case 'keydown':
          if (e.key === 'Escape') this.open = false;
          else if (e.key === 'Tab') this._trapFocus(e);
          break;
        case 'transitionend':
          if (e.target === this._overlay && !this.open) {
            this._afterClose();
          }
          break;
      }
    }

    /* — Attribute-driven state — */
    attributeChangedCallback(name, oldValue, newValue) {
      if (oldValue === newValue) return;
      if (name === 'open') {
        newValue !== null ? this._onShow() : this._onClose();
      }
    }

    get open() {
      return this.hasAttribute('open');
    }

    get contentElement() {
      return this._bodyEl;
    }

    get contentElement() {
      return this._bodyEl;
    }

    set open(val) {
      val ? this.setAttribute('open', '') : this.removeAttribute('open');
    }

    /* — Public API (matches <dialog> convention) — */
    show() {
      if (this.open) {
        this._onShow();
      } else {
        this.open = true;
      }
    }

    close() {
      this.open = false;
    }

    setTitle(text) {
      this.dataset.title = text;
      if (this._titleEl) this._titleEl.textContent = text;
    }

    setContent(html, className = '') {
      const isSameContent = this._slots.body === html;
      this._slots.body = html;
      if (this._bodyEl) {
        if (!isSameContent) this._bodyEl.innerHTML = html;
        this._bodyEl.className = `pdp-info-modal__body ${className}`;
      }
    }

    setClass(className) {
      this._customClass = className ? className.split(/\s+/).filter(Boolean) : [];
      if (this._overlay) {
        this._overlay.classList.add(...this._customClass);
      }
    }

    /* — Private helpers — */
    _onShow() {
      this._triggerEl = document.activeElement;
      this._ensureOverlay();
      requestAnimationFrame(() => {
        this._overlay.classList.add('is-open');
        document.documentElement.classList.add('pdp-scroll-lock');
        document.addEventListener('keydown', this);
        /* Focus the close button or first focusable element */
        const firstFocusable = this._overlay.querySelector('[data-close], button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
        if (firstFocusable) firstFocusable.focus();
      });
      this.dispatchEvent(new CustomEvent('pdp-info-modal:toggle', { bubbles: true, detail: { open: true } }));
    }

    _onClose() {
      if (!this._overlay) return;
      this._overlay.classList.remove('is-open');
      document.removeEventListener('keydown', this);

      this._closeTimer = setTimeout(() => this._afterClose(), 350);
      this._overlay.addEventListener('transitionend', this);

      this.dispatchEvent(new CustomEvent('pdp-info-modal:toggle', { bubbles: true, detail: { open: false } }));
    }

    _afterClose() {
      clearTimeout(this._closeTimer);
      this._closeTimer = null;
      if (this._overlay) this._overlay.removeEventListener('transitionend', this);
      document.documentElement.classList.remove('pdp-scroll-lock');
      /* Restore focus to the element that opened the modal */
      if (this._triggerEl && typeof this._triggerEl.focus === 'function') {
        this._triggerEl.focus();
        this._triggerEl = null;
      }
    }

    /** Trap Tab/Shift+Tab within the modal overlay */
    _trapFocus(e) {
      if (!this._overlay) return;
      const focusable = this._overlay.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last.focus(); }
      } else {
        if (document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    }

    _ensureOverlay() {
      if (this._overlay) return;

      const tpl = document.getElementById('pdp-info-modal-tpl');
      const overlay = tpl.content.firstElementChild.cloneNode(true);
      const header = overlay.querySelector('.pdp-info-modal__header');

      if (this._slots.header) {
        header.innerHTML = this._slots.header;
      } else if (this.dataset.title) {
        const titleEl = header.querySelector('.pdp-info-modal__title');
        if (titleEl) titleEl.textContent = this.dataset.title;
      }

      this._titleEl = overlay.querySelector('.pdp-info-modal__title');
      this._bodyEl = overlay.querySelector('.pdp-info-modal__body');

      if (this._slots.body && this._bodyEl) {
        this._bodyEl.innerHTML = this._slots.body;
      }

      overlay.addEventListener('click', this);
      if (this._customClass) {
        overlay.classList.add(...this._customClass);
      }
      document.body.appendChild(overlay);
      this._overlay = overlay;
    }
  }

  customElements.define('pdp-info-modal', PdpInfoModal);
}

/* ===== PDP Info Button — Opens a PdpInfoModal with inline <template> content ===== */
if (!customElements.get('pdp-info-btn')) {
  class PdpInfoBtn extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      this._modal = null;

      if (!this.hasAttribute('role')) this.setAttribute('role', 'button');
      if (!this.hasAttribute('tabindex')) this.setAttribute('tabindex', '0');

      this.addEventListener('click', this);
      this.addEventListener('keydown', this);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this);
      this.removeEventListener('keydown', this);
    }

    handleEvent(e) {
      const isKeyboardOpen = e.type === 'keydown' && (e.key === 'Enter' || e.key === ' ');
      if (isKeyboardOpen) {
        e.preventDefault();
      }

      if (e.type !== 'click' && !isKeyboardOpen) return;

      const modal = this._getModal();
      this._prepareModal(modal);
      this._showModal(modal);
    }

    _getModal() {
      if (!this._modal) {
        this._modal = document.createElement('pdp-info-modal');
        this.appendChild(this._modal);
      }
      return this._modal;
    }

    _prepareModal(modal) {
      const tpl = this.querySelector('template');
      if (tpl) {
        const html = tpl.innerHTML;
        modal.setTitle(this.dataset.title || tpl.dataset.title || '');
        if (this._modalContent !== html) {
          modal.setContent(html);
          this._modalContent = html;
        }
      }
      if (this.dataset.modalClass) {
        modal.setClass(this.dataset.modalClass);
      }
    }

    _showModal(modal) {
      modal.show();
    }
  }

  customElements.define('pdp-info-btn', PdpInfoBtn);
}

if (!customElements.get('pdp-installment-modal')) {
  class PdpInstallmentModal extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      this._price = parseInt(this.dataset.price, 10);
      this._onLegacyVariantChanged = (e) => this._handleLegacyVariantChanged(e);
      this._onCurrencyUpdate = () => this._syncPrice();
      this.addEventListener('pdp-info-modal:toggle', this);
      document.addEventListener('pdp:price-change', this);
      document.addEventListener('variant:changed', this._onLegacyVariantChanged);
      document.addEventListener('currency:update', this._onCurrencyUpdate);
      this._bindJqueryLegacyEvents();
    }

    disconnectedCallback() {
      this.removeEventListener('pdp-info-modal:toggle', this);
      document.removeEventListener('pdp:price-change', this);
      document.removeEventListener('variant:changed', this._onLegacyVariantChanged);
      document.removeEventListener('currency:update', this._onCurrencyUpdate);
      this._unbindJqueryLegacyEvents();
    }

    handleEvent(e) {
      if (e.type === 'pdp-info-modal:toggle') {
        if (e.detail?.open && e.target.matches('pdp-info-modal')) this._syncPrice(e.target.contentElement);
      } else if (e.type === 'pdp:price-change') {
        const price = e.detail?.price;
        this._setPrice(price);
      }
    }

    _handleLegacyVariantChanged(e) {
      const variant = e.currentVariant || e.originalEvent?.currentVariant || e.detail?.variant || e.detail;
      if (!variant || variant === 'nathan') return;
      this._setPrice(variant.price);
    }

    _setPrice(price) {
      if (typeof price !== 'number' || isNaN(price)) return;
      this._price = price;
      this.dataset.price = String(this._price);
      this._syncPrice();
    }

    _bindJqueryLegacyEvents() {
      const $ = window.jQuery_T4NT || window.jQuery;
      if (!$) return;

      this._jqNamespace = `.pdpInstallment${Math.random().toString(36).slice(2)}`;
      this._$legacyVariantTarget = $(this.closest('[data-product-featured]') || document);
      this._$legacyCurrencyTargets = $(document).add(document.body);
      this._$legacyVariantTarget.on(`variant:changed${this._jqNamespace}`, this._onLegacyVariantChanged);
      this._$legacyCurrencyTargets.on(`currency:update${this._jqNamespace}`, this._onCurrencyUpdate);
    }

    _unbindJqueryLegacyEvents() {
      if (!this._jqNamespace) return;
      if (this._$legacyVariantTarget) this._$legacyVariantTarget.off(this._jqNamespace);
      if (this._$legacyCurrencyTargets) this._$legacyCurrencyTargets.off(this._jqNamespace);
      this._jqNamespace = null;
      this._$legacyVariantTarget = null;
      this._$legacyCurrencyTargets = null;
    }

    _syncPrice(content = null) {
      const price = this._price;
      if (typeof price !== 'number' || isNaN(price)) return;

      content ??= this.querySelector('pdp-info-modal')?.contentElement;
      if (!content) return;

      const priceValue = String(price);
      content.querySelectorAll('pdp-installment-klarna, pdp-installment-paypal').forEach((el) => {
        el.setAttribute('data-price', priceValue);
      });
    }
  }

  customElements.define('pdp-installment-modal', PdpInstallmentModal);
}

/**
 * Fetch a Shopify section URL and return its inner HTML.
 * Optionally strips a lazy-load split marker (e.g. T4S `[t4splitlz]`).
 * @param {string} url
 * @param {object} [opts]
 * @param {string} [opts.splitMarker='[t4splitlz]'] - Marker to split on; set to `null` to skip.
 * @returns {Promise<string>}
 */
async function pdpFetchSection(url, { splitMarker = '[t4splitlz]' } = {}) {
  const res = await fetch(url);
  if (!res.ok) throw new Error(res.status);
  let html = await res.text();
  if (splitMarker && html.indexOf(splitMarker) > -1) html = html.split(splitMarker)[1] || html;
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return (doc.querySelector('.shopify-section') || doc.body).innerHTML;
}

/* ===== PDP Guide Link Web Component ===== */
if (!customElements.get('pdp-guide-link')) {
  class PdpGuideLink extends HTMLElement {
    static _store = new Map();

    connectedCallback() {
      if (this._init) return;
      this._init = true;
      if (!this.hasAttribute('role')) this.setAttribute('role', 'button');
      if (!this.hasAttribute('tabindex')) this.setAttribute('tabindex', '0');
      this.addEventListener('click', this);
      this.addEventListener('keydown', this);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this);
      this.removeEventListener('keydown', this);
    }

    handleEvent(e) {
      if (e.type === 'click') {
        this._showModal();
      } else if (e.type === 'keydown' && (e.key === 'Enter' || e.key === ' ')) {
        e.preventDefault();
        this._showModal();
      }
    }

    _getModal() {
      const key = this.dataset.storageid;
      let modal = key ? PdpGuideLink._store.get(key) : null;
      if (!modal) {
        modal = document.createElement('pdp-info-modal');
        modal.dataset.title = this.dataset.modalTitle || '';
        modal.setClass(this.dataset.modalClass || '');
        if (key) PdpGuideLink._store.set(key, modal);
      }
      return modal;
    }

    async _showModal() {
      const modal = this._getModal();
      const src = this.dataset.src;

      if (src && !modal._contentLoaded) {
        modal.setContent('<div class="pdp-guide-link__loading"></div>', 'pdp-guide-link__body--loading');
        modal.show();
        try {
          const content = await pdpFetchSection(src);
          modal.setContent(`<div class="pdp-ctx">${content}</div>`, 'pdp-guide-link');
          modal._contentLoaded = true;
        } catch {
          modal.setContent('<p style="padding:20px;text-align:center">Failed to load content.</p>');
        }
      } else {
        modal.show();
      }
    }
  }

  customElements.define('pdp-guide-link', PdpGuideLink);
}

/* ===== PDP Guarantees Web Component ===== */
if (!customElements.get('pdp-guarantees')) {
  class PdpGuarantees extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      this._modal = null;
      this.addEventListener('click', this);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this);
    }

    handleEvent(e) {
      if (e.type === 'click') {
        const btn = e.target.closest('.pdp-product__guarantee-header');
        if (!btn) return;
        const item = btn.closest('.pdp-product__guarantee-item');
        if (!item) return;
        const tpl = item.querySelector('template');
        if (!tpl) return;
        this._getModal().setTitle(tpl.dataset.title || '');
        this._getModal().setContent(tpl.innerHTML);
        this._getModal().show();
      }
    }

    _getModal() {
      if (!this._modal) {
        this._modal = document.createElement('pdp-info-modal');
        this._modal.setClass('pdp-rich-text');
        this.appendChild(this._modal);
      }
      return this._modal;
    }
  }

  customElements.define('pdp-guarantees', PdpGuarantees);
}

/* ===== PDP Section Loader Web Component ===== */
if (!customElements.get('pdp-section-loader')) {
  const SPLIT_MARKER = '<!-- [pdp_section_split] -->';

  class PdpSectionLoader extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      this._loaded = false;

      const trigger = this.dataset.trigger;

      if (trigger) {
        this._onTriggerClick = (e) => {
          if (e.target.closest(trigger)) {
            e.preventDefault();
            this._load();
          }
        };
        document.addEventListener('click', this._onTriggerClick);
      } else if (this.hasAttribute('data-lazy')) {
        this._observer = new IntersectionObserver(
          (entries) => {
            if (entries[0].isIntersecting) {
              this._observer.disconnect();
              this._load();
            }
          },
          { rootMargin: '200px' }
        );
        this._observer.observe(this);
      } else if (this.hasAttribute('data-idle')) {
        const schedule = window.requestIdleCallback || ((cb) => setTimeout(cb, 1));
        this._idleId = schedule(() => this._load(), { timeout: 3000 });
      } else if (this.children.length) {
        this._onChildClick = (e) => {
          e.preventDefault();
          this._load();
        };
        this.addEventListener('click', this._onChildClick, { once: true });
      } else {
        this._load();
      }
    }

    disconnectedCallback() {
      if (this._onTriggerClick) {
        document.removeEventListener('click', this._onTriggerClick);
      }
      if (this._onChildClick) {
        this.removeEventListener('click', this._onChildClick);
      }
      if (this._observer) {
        this._observer.disconnect();
      }
      if (this._idleId) {
        const cancel = window.cancelIdleCallback || clearTimeout;
        cancel(this._idleId);
        this._idleId = null;
      }
    }

    _load() {
      if (this._loaded || this.hasAttribute('loading')) return;

      const sectionId = this.dataset.sectionId;
      if (!sectionId) return;

      this.setAttribute('loading', '');
      this.dispatchEvent(new CustomEvent('pdp-section:loading', { bubbles: true }));

      const baseUrl = this.dataset.url || window.location.pathname;
      const sep = baseUrl.includes('?') ? '&' : '?';
      const url = `${baseUrl}${sep}section_id=${encodeURIComponent(sectionId)}`;

      pdpFetchSection(url, { splitMarker: SPLIT_MARKER })
        .then((innerHTML) => {
          this._inject(innerHTML);
          this._loaded = true;
          this.removeAttribute('loading');
          this.setAttribute('loaded', '');
          if (this._onTriggerClick) {
            document.removeEventListener('click', this._onTriggerClick);
            this._onTriggerClick = null;
          }
          this.dispatchEvent(new CustomEvent('pdp-section:loaded', { bubbles: true }));
        })
        .catch((err) => {
          console.warn(`[pdp-section-loader] Failed to load "${sectionId}":`, err);
          this.removeAttribute('loading');
          this.setAttribute('error', '');
          this.dispatchEvent(new CustomEvent('pdp-section:error', { bubbles: true, detail: { error: err } }));
        });
    }

    _inject(raw) {
      let root;
      if (this.hasAttribute('data-replace')) {
        this.innerHTML = raw;
        root = this;
      } else {
        const prev = this.querySelector('[data-loaded-content]');
        if (prev) prev.remove();
        root = document.createElement('div');
        root.setAttribute('data-loaded-content', '');
        root.style.display = 'contents';
        root.innerHTML = raw;
        this.appendChild(root);
      }

      const sid = this.dataset.sectionId;
      root.querySelectorAll('style').forEach((s) => {
        if (!document.head.querySelector(`style[data-section="${sid}"]`)) {
          s.dataset.section = sid;
          document.head.appendChild(s);
        } else {
          s.remove();
        }
      });

      root.querySelectorAll('script').forEach((old) => {
        const s = document.createElement('script');
        for (const attr of old.attributes) s.setAttribute(attr.name, attr.value);
        s.textContent = old.textContent;
        old.replaceWith(s);
      });
    }

    reload() {
      this._loaded = false;
      this.removeAttribute('loaded');
      this.removeAttribute('error');
      this._load();
    }
  }

  customElements.define('pdp-section-loader', PdpSectionLoader);
}

/* ===== PDP Thumb Strip — Generic scrollable thumbnail strip ===== */
if (!customElements.get('pdp-thumb-strip')) {
  class PdpThumbStrip extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      this._activeIndex = 0;

      this._dragging = false;
      this._didDrag = false;
      this._startX = 0;
      this._scrollStart = 0;

      this.addEventListener('click', this);
      this.addEventListener('mousedown', this);
      this.addEventListener('pointerdown', this);
      this._syncActive(0, false);
      this._checkScrollable();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this);
      this.removeEventListener('mousedown', this);
      this.removeEventListener('pointerdown', this);
    }

    handleEvent(e) {
      switch (e.type) {
        case 'pointerdown':
          e.stopPropagation();
          break;
        case 'mousedown':
          if (e.button !== 0) return;
          this._dragging = true;
          this._didDrag = false;
          this._startX = e.clientX;
          this._scrollStart = this.scrollLeft;
          this.style.cursor = 'grabbing';
          this._onMouseMove ??= (ev) => this._handleMouseMove(ev);
          this._onMouseUp ??= (ev) => this._handleMouseUp(ev);
          window.addEventListener('mousemove', this._onMouseMove);
          window.addEventListener('mouseup', this._onMouseUp);
          e.preventDefault();
          break;
        case 'click': {
          if (this._didDrag) return;
          const item = e.target.closest('[data-thumb-index]');
          if (!item) return;
          const index = parseInt(item.dataset.thumbIndex, 10);
          if (isNaN(index) || index === this._activeIndex) return;
          this.setActive(index, true);
          this.dispatchEvent(new CustomEvent('thumb:change', { bubbles: true, detail: { index } }));
          break;
        }
      }
    }

    _handleMouseMove(e) {
      if (!this._dragging) return;
      const dx = e.clientX - this._startX;
      if (Math.abs(dx) > 3) this._didDrag = true;
      this.scrollLeft = this._scrollStart - dx;
    }

    _handleMouseUp() {
      this._dragging = false;
      this.style.cursor = '';
      window.removeEventListener('mousemove', this._onMouseMove);
      window.removeEventListener('mouseup', this._onMouseUp);
    }

    /** @param {number} index  @param {boolean} [scroll=true]  @param {boolean} [animate=true] */
    setActive(index, scroll = true, animate = true) {
      this._activeIndex = index;
      this._syncActive(index, scroll, animate);
    }

    get activeIndex() {
      return this._activeIndex;
    }

    /**
     * Build thumbnails from an array of {src, alt} objects.
     * Replaces children inside the scroller.
     */
    renderItems(items) {
      this.innerHTML = '';

      items.forEach((item, i) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'pdp-thumb-strip__item';
        btn.dataset.thumbIndex = i;
        btn.setAttribute('aria-label', `Image ${i + 1}`);
        const img = document.createElement('img');
        img.src = item.src;
        img.alt = item.alt || '';
        img.loading = 'lazy';
        img.draggable = false;
        btn.append(img);
        this.append(btn);
      });

      this._syncActive(0, false);
      this._checkScrollable();
    }

    _checkScrollable() {
      this.classList.toggle('is-scrollable', this.scrollWidth > this.clientWidth);
    }

    _syncActive(index, scroll, animate = true) {
      const items = this.querySelectorAll('[data-thumb-index]');
      items.forEach((el, i) => el.classList.toggle('is-active', i === index));

      if (!scroll) return;
      const active = items[index];
      if (!active) return;
      requestAnimationFrame(() => {
        const containerRect = this.getBoundingClientRect();
        const activeRect = active.getBoundingClientRect();
        const offset = this.scrollLeft + (activeRect.left - containerRect.left) - (containerRect.width - activeRect.width) / 2;
        this.scrollTo({ left: Math.max(0, offset), behavior: animate ? 'smooth' : 'instant' });
      });
    }
  }

  customElements.define('pdp-thumb-strip', PdpThumbStrip);
}

/* ===== PDP Quantity Stepper Web Component ===== */
if (!customElements.get('pdp-qty-stepper')) {
  class PdpQtyStepper extends HTMLElement {
    static get observedAttributes() {
      return ['min', 'max', 'step', 'value', 'disabled'];
    }

    connectedCallback() {
      if (this._init) return;
      this._init = true;

      this._minusBtn = this.querySelector('[data-action="minus"]');
      this._plusBtn = this.querySelector('[data-action="plus"]');
      this._input = this.querySelector('input');

      if (!this._input) return;

      this.addEventListener('click', this);
      this._input.addEventListener('change', this);
      this._updateState();
    }

    handleEvent(e) {
      if (e.type === 'click') {
        const btn = e.target.closest('[data-action]');
        if (!btn || this.hasAttribute('disabled')) return;
        const action = btn.dataset.action;
        const cur = parseInt(this._input.value, 10) || this._min;
        const next = action === 'minus'
          ? Math.max(this._min, cur - this._step)
          : Math.min(this._max, cur + this._step);
        if (next !== cur) {
          this._input.value = next;
          this._updateState();
          this._dispatch();
        }
      } else if (e.type === 'change') {
        let val = parseInt(this._input.value, 10);
        val = this._clamp(isNaN(val) ? this._min : val);
        this._input.value = val;
        this._updateState();
        this._dispatch();
      }
    }

    attributeChangedCallback(name) {
      if (!this._init) return;
      if (name === 'value') {
        const val = this._clamp(parseInt(this.getAttribute('value'), 10) || this._min);
        if (this._input) this._input.value = val;
      }
      if (name === 'max' || name === 'min' || name === 'step') {
        if (this._input) {
          this._input.min = this._min;
          this._input.max = this._max;
          this._input.step = this._step;
          const clamped = this._clamp(parseInt(this._input.value, 10));
          if (clamped !== parseInt(this._input.value, 10)) this._input.value = clamped;
        }
      }
      this._updateState();
    }

    get _min() { return parseInt(this.getAttribute('min'), 10) || 1; }
    get _max() { return parseInt(this.getAttribute('max'), 10) || 9999; }
    get _step() { return parseInt(this.getAttribute('step'), 10) || 1; }

    get value() { return this._input ? parseInt(this._input.value, 10) : this._min; }
    set value(v) {
      const val = this._clamp(parseInt(v, 10) || this._min);
      if (this._input) this._input.value = val;
      this._updateState();
    }

    _clamp(val) {
      return Math.max(this._min, Math.min(this._max, val));
    }

    _updateState() {
      if (!this._minusBtn || !this._plusBtn) return;
      const val = parseInt(this._input.value, 10);
      this._minusBtn.disabled = val <= this._min || this.hasAttribute('disabled');
      this._plusBtn.disabled = val >= this._max || this.hasAttribute('disabled');
      this._minusBtn.classList.toggle('is-disabled', this._minusBtn.disabled);
      this._plusBtn.classList.toggle('is-disabled', this._plusBtn.disabled);
    }

    _dispatch() {
      this.dispatchEvent(new CustomEvent('qty:change', {
        bubbles: true,
        detail: { value: this.value }
      }));
    }

    disconnectedCallback() {
      this.removeEventListener('click', this);
      if (this._input) this._input.removeEventListener('change', this);
    }
  }

  customElements.define('pdp-qty-stepper', PdpQtyStepper);
}

/**
 * PDP global loading bus — use one event for all loading UI sync (sticky bar, etc.).
 * @param {'atc'|'buy_now'} scope
 * @param {boolean} active - true=start loading, false=end
 */
function dispatchPdpLoading(scope, active) {
  document.dispatchEvent(new CustomEvent('pdp:loading', {
    bubbles: true,
    detail: { scope, active: !!active }
  }));
}

/**
 * PDP purchase intents from sticky bar / compare modules / custom code → main form handler.
 * Vocabulary matches {@link dispatchPdpLoading} scopes: `'atc' | 'buy_now'`.
 * @param {'atc'|'buy_now'} action
 */
function dispatchPdpProductAction(action) {
  document.dispatchEvent(new CustomEvent('pdp:product-action', {
    bubbles: true,
    detail: { action }
  }));
}

window.dispatchPdpLoading = dispatchPdpLoading;
window.dispatchPdpProductAction = dispatchPdpProductAction;

if (!customElements.get('pdp-product-form')) {
  class PdpProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.variantInput = this.querySelector('[data-pdp-variant-input]');
      this.variants = JSON.parse(this.querySelector('[data-pdp-variants]').textContent);
      this.currentVariant = this.variants.find(v => String(v.id) === this.variantInput.value) || this.variants[0];

      this._bindSwatches();
      this._bindQuantity();
      this._bindForm();
      this._bindBuyNow();
      this._bindExternalEvents();

      /* Dispatch initial variant so PdpPrice, PdpCoupon, etc. get data on page load.
         Use queueMicrotask so all customElements.define() calls finish first
         and every listener (e.g. PdpPrice, defined later) is already registered. */
      queueMicrotask(() => this._dispatchVariantChanged(this.currentVariant, null, true));
    }

    disconnectedCallback() {
      if (this._optionChangeFrame) {
        window.cancelAnimationFrame(this._optionChangeFrame);
        this._optionChangeFrame = null;
      }
      if (this._optionChangeTimer) {
        window.clearTimeout(this._optionChangeTimer);
        this._optionChangeTimer = null;
      }
      document.removeEventListener('pdp:product-action', this._onExternalProductAction);
      if (this._pageShowHandler) {
        window.removeEventListener('pageshow', this._pageShowHandler);
      }
    }

    /* ── Swatch Selection ── */

    _bindSwatches() {
      this.querySelectorAll('[data-pdp-swatch]').forEach(swatch => {
        swatch.addEventListener('click', (e) => {
          e.preventDefault();
          if (swatch.classList.contains('is-soldout')) return;

          const optionEl = swatch.closest('[data-pdp-option]');
          optionEl.querySelectorAll('[data-pdp-swatch]').forEach(s => {
            s.classList.remove('is-active');
            if (s.hasAttribute('aria-pressed')) s.setAttribute('aria-pressed', 'false');
          });
          swatch.classList.add('is-active');
          if (swatch.hasAttribute('aria-pressed')) swatch.setAttribute('aria-pressed', 'true');

          const currentValueEl = optionEl.querySelector('[data-pdp-current-value]');
          if (currentValueEl) currentValueEl.textContent = swatch.dataset.value;

          this._scheduleOptionChange();
        });
      });
    }

    _scheduleOptionChange() {
      if (this._optionChangeFrame) window.cancelAnimationFrame(this._optionChangeFrame);
      if (this._optionChangeTimer) window.clearTimeout(this._optionChangeTimer);

      this._optionChangeFrame = window.requestAnimationFrame(() => {
        this._optionChangeFrame = null;
        this._optionChangeTimer = window.setTimeout(() => {
          this._optionChangeTimer = null;
          this._onOptionChange();
        }, 0);
      });
    }

    _onOptionChange() {
      const selectedOptions = [];
      this.querySelectorAll('[data-pdp-option]').forEach(optionEl => {
        const active = optionEl.querySelector('[data-pdp-swatch].is-active');
        if (active) {
          selectedOptions.push({
            index: parseInt(optionEl.dataset.optionIndex, 10),
            value: active.dataset.value
          });
        }
      });

      const oldVariant = this.currentVariant;
      const newVariant = this.variants.find(v =>
        selectedOptions.every(o => v['option' + (o.index + 1)] === o.value)
      );

      if (!newVariant) return;

      this.currentVariant = newVariant;
      this.variantInput.value = newVariant.id;

      this._updateAtcButton(newVariant);
      this._updateSwatchAvailability();
      this._updateQuantityMax(newVariant);
      this._updateUrl(newVariant);

      this._dispatchVariantChanged(newVariant, oldVariant);
    }

    /* ── Availability Cross-Check ── */

    _updateSwatchAvailability() {
      const optionEls = Array.from(this.querySelectorAll('[data-pdp-option]'));

      optionEls.forEach(optionEl => {
        const optionIndex = parseInt(optionEl.dataset.optionIndex, 10);
        const swatches = optionEl.querySelectorAll('[data-pdp-swatch]');

        swatches.forEach(swatch => {
          const testValue = swatch.dataset.value;
          const hasAvailable = this.variants.some(v => {
            if (v['option' + (optionIndex + 1)] !== testValue) return false;
            let matches = true;
            optionEls.forEach(otherEl => {
              const otherIndex = parseInt(otherEl.dataset.optionIndex, 10);
              if (otherIndex === optionIndex) return;
              const otherActive = otherEl.querySelector('[data-pdp-swatch].is-active');
              if (otherActive && v['option' + (otherIndex + 1)] !== otherActive.dataset.value) {
                matches = false;
              }
            });
            return matches && v.available;
          });
          swatch.classList.toggle('is-soldout', !hasAvailable);
        });
      });
    }

    /* ── ATC Button State ── */

    _updateAtcButton(variant) {
      const btn = this.querySelector('[data-pdp-atc]');
      const textEl = this.querySelector('[data-pdp-atc-text]');
      const dotEl = this.querySelector('[data-pdp-atc-dot]');
      const priceEl = this.querySelector('[data-pdp-atc-price]');
      if (!btn) return;

      const addText = btn.closest('form')?.dataset?.addToCartText || 'Add to cart';

      if (!variant || !variant.available) {
        btn.setAttribute('aria-disabled', 'true');
        btn.setAttribute('disabled', '');
        if (textEl) textEl.textContent = window.variantStrings?.soldOut || 'Sold out';
        if (dotEl) dotEl.hidden = true;
        if (priceEl) priceEl.hidden = true;
      } else {
        btn.removeAttribute('aria-disabled');
        btn.removeAttribute('disabled');
        if (textEl) textEl.textContent = addText;
        if (dotEl) dotEl.hidden = false;
        if (priceEl) priceEl.hidden = false;
      }

      const buyNowBtn = this.querySelector('[data-pdp-buy-now]');
      if (buyNowBtn) {
        if (!variant || !variant.available) {
          buyNowBtn.setAttribute('aria-disabled', 'true');
          buyNowBtn.setAttribute('disabled', '');
          buyNowBtn.classList.add('is-disabled');
        } else {
          buyNowBtn.removeAttribute('aria-disabled');
          buyNowBtn.removeAttribute('disabled');
          buyNowBtn.classList.remove('is-disabled');
        }
      }
    }

    /* ── Quantity ── */

    _bindQuantity() {
      this._qtyStepper = this.querySelector('pdp-qty-stepper');
    }

    _updateQuantityMax(variant) {
      if (!this._qtyStepper || !variant) return;

      let max = 9999;
      if (variant.inventory_management && variant.inventory_quantity > 0 && variant.inventory_policy !== 'continue') {
        max = variant.inventory_quantity;
      }

      this._qtyStepper.setAttribute('max', max);
    }

    /* ── Form Submit ── */

    _bindForm() {
      if (!this.form) return;
      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        const btn = this.querySelector('[data-pdp-atc]');
        if (btn && (btn.hasAttribute('disabled') || btn.getAttribute('aria-disabled') === 'true')) return;
        this._submitAjaxCart(btn);
      });
    }

    _bindBuyNow() {
      const btn = this.querySelector('[data-pdp-buy-now]');
      if (!btn) return;
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        if (btn.getAttribute('aria-disabled') === 'true' || btn.hasAttribute('disabled')) return;
        this.buyNow();
      });

      this._pageShowHandler = (e) => {
        if (!e.persisted) return;
        this._setBuyNowLoading(false);
      };
      window.addEventListener('pageshow', this._pageShowHandler);
    }

    _setBuyNowLoading(isLoading) {
      const btn = this.querySelector('[data-pdp-buy-now]');
      if (!btn) return;
      const spinner = btn.querySelector('[data-pdp-buy-now-spinner]');
      if (spinner) spinner.classList.toggle('is-active', isLoading);
      btn.classList.toggle('is--loading', isLoading);
      dispatchPdpLoading('buy_now', isLoading);
      if (isLoading) {
        btn.setAttribute('aria-disabled', 'true');
        btn.setAttribute('disabled', '');
      } else {
        this._updateAtcButton(this.currentVariant);
      }
    }

    _submitAjaxCart(btn) {
      /* Loading state (matches T4S is--loading / aria-disabled pattern) */
      if (btn) {
        btn.classList.add('is--loading');
        btn.setAttribute('aria-disabled', 'true');
      }
      const spinner = this.querySelector('[data-pdp-atc-spinner]');
      if (spinner) spinner.classList.add('is-active');
      dispatchPdpLoading('atc', true);

      /* Build fetch config (T4S pattern: FormData, no Content-Type) */
      const config = {
        method: 'POST',
        headers: {
          'Accept': 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest'
        }
      };

      const formData = new FormData(this.form);

      /* Append cart sections for section rendering API */
      const cartType = window.T4Sconfigs?.cartType;
      const sectionsStr = cartType === 'disable' ? 'cart_data' : 'cart_data,mini_cart';
      formData.append('sections', sectionsStr.split(','));
      formData.append('sections_url', window.location.pathname);
      config.body = formData;

      const addUrl = (window.T4Sroutes?.cart_add_url || '/cart/add') + '.js';

      /* jQuery ref for T4S event triggers */
      const $ = window.jQuery_T4NT || window.jQuery;
      const $doc = $ ? $(document) : null;

      const clearLoading = () => {
        if (btn) {
          btn.classList.remove('is--loading');
          btn.removeAttribute('aria-disabled');
        }
        if (spinner) spinner.classList.remove('is-active');
        dispatchPdpLoading('atc', false);
      };

      /* Listen for cart:updated to clear loading (T4S pattern) */
      if ($doc) {
        $doc.on('cart:updated.pdpForm', () => {
          clearLoading();
          $doc.off('cart:updated.pdpForm');
        });
      }

      fetch(addUrl, config)
        .then(res => res.json())
        .then(data => {
          if (data.status) {
            /* Error response from Shopify */
            if (data.description && !window.T4Sconfigs?.disATCerror && typeof T4SThemeSP !== 'undefined') {
              T4SThemeSP.Notices(data.description);
            }
            if ($doc) $doc.trigger('cart:updated', ['error']);
            else clearLoading();
            return;
          }
          /* Success — trigger T4S events & update cart drawer */
          if ($doc) $doc.trigger('add:cart:success').trigger('add:cart:upsell');
          if (typeof T4SThemeSP !== 'undefined') {
            T4SThemeSP.isATCSuccess = true;
            if (data.sections) {
              T4SThemeSP.Cart.renderContents(data.sections);
            } else {
              T4SThemeSP.Cart.getToFetch();
            }
          }
        })
        .catch(err => {
          console.error('PDP ATC error:', err);
          if ($doc) $doc.trigger('cart:updated');
          else clearLoading();
        });
    }

    /* ── URL Update ── */

    _updateUrl(variant) {
      if (!variant || !window.history.replaceState) return;
      const url = new URL(window.location);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    }

    /* ── Public API ── */

    addToCart() {
      const btn = this.querySelector('[data-pdp-atc]');
      if (btn && (btn.hasAttribute('disabled') || btn.getAttribute('aria-disabled') === 'true')) return;
      this._submitAjaxCart(btn);
    }

    buyNow() {
      if (!this.currentVariant?.available) return;
      this._setBuyNowLoading(true);
      window.location.href = '/cart/' + this.currentVariant.id + ':' + this._getQuantity();
    }

    _getQuantity() {
      return this._qtyStepper ? this._qtyStepper.value : 1;
    }

    _bindExternalEvents() {
      this._onExternalProductAction = (e) => {
        const action = e.detail && e.detail.action;
        if (action === 'atc') this.addToCart();
        else if (action === 'buy_now') this.buyNow();
      };
      document.addEventListener('pdp:product-action', this._onExternalProductAction);
    }

    /* ── Variant Event Dispatch ── */

    _dispatchVariantChanged(newVariant, oldVariant, isInit = false) {
      const event = new CustomEvent('variant:changed', {
        bubbles: true,
        detail: newVariant
      });
      event.currentVariant = newVariant;
      event.oldVariant = oldVariant;
      event.isInit = isInit;
      event.quantity = this._getQuantity();
      this.form.dispatchEvent(event);
    }
  }

  customElements.define('pdp-product-form', PdpProductForm);
}

/* ===== PDP Price — Auto-update on variant change ===== */
if (!customElements.get('pdp-price')) {
  class PdpPrice extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      this._coupon = null;
      this._lastVariant = null;
      this._onVariantChanged = this._onVariantChanged.bind(this);
      this._onCouponApplied = this._onCouponApplied.bind(this);
      this._onCouponRemoved = this._onCouponRemoved.bind(this);
      document.addEventListener('variant:changed', this._onVariantChanged);
      document.addEventListener('pdp:coupon-applied', this._onCouponApplied);
      document.addEventListener('pdp:coupon-removed', this._onCouponRemoved);
    }

    disconnectedCallback() {
      document.removeEventListener('variant:changed', this._onVariantChanged);
      document.removeEventListener('pdp:coupon-applied', this._onCouponApplied);
      document.removeEventListener('pdp:coupon-removed', this._onCouponRemoved);
    }

    _onCouponApplied(e) {
      this._coupon = e.detail;
      this._render();
    }

    _onCouponRemoved() {
      this._coupon = null;
      this._render();
    }

    _onVariantChanged(e) {
      const variant = e.currentVariant || (e.detail && e.detail.variant);
      if (!variant) return;
      const isInit = !this._lastVariant;
      this._lastVariant = variant;
      // Render on init too when a coupon is already applied (browser-restored checkbox)
      if (!isInit || this._coupon) this._render();
    }

    _applyDiscount(price) {
      if (!this._coupon) return price;
      const { discountType, discountValue } = this._coupon;
      if (discountType === 'percentage') {
        return Math.ceil(price * (1 - discountValue / 100));
      }
      return Math.max(0, price - discountValue * 100);
    }

    _render() {
      const variant = this._lastVariant;

      if (!variant) return;

      const fmt = (cents, format) => T4SThemeSP.Currency?.formatMoney(cents, format) || '';
      const compareFormat = this.dataset.compareFormat || window.__pdp__config__?.moneyFormat;
      const finalPrice = this._applyDiscount(variant.price);

      const priceEl = this.querySelector('[data-pdp-current-price]');
      if (priceEl) priceEl.textContent = fmt(finalPrice);

      /* compare price: compare_at_price takes priority;
         when no compare_at_price but coupon active, show original price as compare */
      const compareEl = this.querySelector('[data-pdp-compare-price]');
      const compareValue = variant.compare_at_price || (this._coupon ? variant.price : null);
      const hasCompare = compareValue && compareValue > finalPrice;

      if (hasCompare) {
        if (compareEl) {
          compareEl.textContent = fmt(compareValue, compareFormat);
          compareEl.hidden = false;
        } else if (priceEl) {
          const span = document.createElement('span');
          span.className = 'pdp-product__compare-price';
          span.setAttribute('data-pdp-compare-price', '');
          span.textContent = fmt(compareValue, compareFormat);
          priceEl.after(span);
        }
      } else if (compareEl) {
        compareEl.hidden = true;
      }

      const atcPriceEl = this.querySelector('[data-pdp-atc-price]');
      if (atcPriceEl) atcPriceEl.textContent = fmt(finalPrice);

      this.dispatchEvent(new CustomEvent('pdp:price-change', {
        bubbles: true,
        detail: { price: finalPrice, variant, coupon: this._coupon }
      }));
    }
  }

  customElements.define('pdp-price', PdpPrice);
}

/* ── Tooltip (generic) ── */
if (!customElements.get('pdp-tooltip')) {
  class PdpTooltip extends HTMLElement {
    connectedCallback() {
      this._trigger = this.querySelector('[data-tooltip-trigger]');
      this._content = this.querySelector('[data-tooltip-content]');
      if (!this._trigger || !this._content) return;

      this._trigger.addEventListener('click', () => {
        const opening = !this._content.classList.contains('is-open');
        this._content.classList.toggle('is-open');
        if (opening) this._position();
      });

      this._onOutsideClick = (e) => {
        if (!this.contains(e.target)) this.close();
      };
      document.addEventListener('click', this._onOutsideClick);
    }

    disconnectedCallback() {
      if (this._onOutsideClick) document.removeEventListener('click', this._onOutsideClick);
    }

    close() {
      if (this._content) this._content.classList.remove('is-open');
    }

    /** Reposition tooltip so it stays within the viewport */
    _position() {
      const s = this._content.style;
      /* reset to natural position so we can measure */
      s.removeProperty('--tt-left');
      s.removeProperty('--tt-right');

      const rect = this._content.getBoundingClientRect();
      const pad = 8; /* minimum gap from viewport edge */

      if (rect.right > window.innerWidth - pad) {
        /* overflows right → anchor to right edge of trigger */
        s.setProperty('--tt-right', '0');
        s.setProperty('--tt-left', 'auto');
      } else if (rect.left < pad) {
        /* overflows left → anchor to left edge of trigger */
        s.setProperty('--tt-left', '0');
        s.setProperty('--tt-right', 'auto');
      }
    }
  }

  customElements.define('pdp-tooltip', PdpTooltip);
}

/* ── Ship To ── */
if (!customElements.get('pdp-ship-to')) {
  class PdpShipTo extends HTMLElement {
    connectedCallback() {
      this._trigger = this.querySelector('[data-ship-trigger]');
      this._dropdown = this.querySelector('[data-ship-dropdown]');
      this._currentLabel = this.querySelector('[data-ship-current]');
      this._form = this.querySelector('#pdp-ship-to-form');
      this._countryInput = this.querySelector('[data-ship-country-input]');

      if (this._trigger) {
        this._trigger.addEventListener('click', () => this._toggleDropdown());
      }
      if (this._dropdown) {
        this._dropdown.addEventListener('click', (e) => {
          const opt = e.target.closest('[data-ship-option]');
          if (opt) this._selectCountry(opt);
        });
      }

      /* Close dropdown when tooltip inside is clicked */
      this.addEventListener('click', (e) => {
        if (e.target.closest('pdp-tooltip')) this._closeDropdown();
      });

      this._onOutsideClick = (e) => {
        if (!this._trigger.contains(e.target)) this._closeDropdown();
      };
    }

    disconnectedCallback() {
      document.removeEventListener('click', this._onOutsideClick);
    }

    _toggleDropdown() {
      const isOpen = this._dropdown.classList.toggle('is-open');
      this._trigger.setAttribute('aria-expanded', String(isOpen));
      this.querySelector('pdp-tooltip')?.close();
      if (isOpen) {
        document.addEventListener('click', this._onOutsideClick);
      } else {
        document.removeEventListener('click', this._onOutsideClick);
      }
    }

    _closeDropdown() {
      if (this._dropdown) {
        this._dropdown.classList.remove('is-open');
        this._trigger.setAttribute('aria-expanded', 'false');
      }
      document.removeEventListener('click', this._onOutsideClick);
    }

    _selectCountry(opt) {
      const code = opt.dataset.countryCode;
      if (!code || !this._form || !this._countryInput) return;

      this._dropdown.querySelectorAll('[data-ship-option]').forEach((o) => {
        o.classList.remove('is-active');
        o.setAttribute('aria-selected', 'false');
      });
      opt.classList.add('is-active');
      opt.setAttribute('aria-selected', 'true');
      this._currentLabel.textContent = opt.textContent.trim();
      this._closeDropdown();

      this._countryInput.value = code;
      this._form.submit();
    }
  }

  customElements.define('pdp-ship-to', PdpShipTo);
}